<script setup lang="ts">
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/dropdown-menu'
import { ref, computed, onMounted, watch } from 'vue'
import { useLibraryStore } from '@/library/library.store'
import ProgressSpinner from 'primevue/progressspinner'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import SaveToFolderModal from '@/components/SaveToFolderModal.vue'
import { getAnalyticsTracker } from "@/tracking/analytics/plugin";
import { CONTENT_TYPES } from '@/util/access_visibility'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

const analytics = getAnalyticsTracker()

const emit = defineEmits<{
  save: [folder: { uuid: string, name: string }]
  saveerror: [error: unknown]
}>()

const props = defineProps({
  contentType: {
    type: String,
    required: true,
  },
  contentTypeLabel: {
    type: String,
    required: true,
  },
  contentUuid: {
    type: String,
    required: true,
  },
  size: {
    type: String,
    default: 'md',
    validator: (value: string) => ['md', 'xs'].includes(value)
  }
})

const libraryStore = useLibraryStore()
const userStore = useUserStore()
const { currentFolder } = storeToRefs(libraryStore)
const { userId } = storeToRefs(userStore)

const recentFolders = computed(()=> {
  if (props.contentType === CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY) {
    return libraryStore.recentUserOwnedFolders
  }
  return libraryStore.recentFolders
})

const showSaveToFolderModal = ref(false)
const isSaving = ref(false)

onMounted(async () => {
  if (userId.value) {
    await libraryStore.fetchRecentFolders(userId.value)
    await libraryStore.fetchFolders(userId.value)
  }
})

watch(userId, async (newUserId) => {
  if (newUserId) {
    await libraryStore.fetchRecentFolders(newUserId)
    await libraryStore.fetchFolders(newUserId)
  }
})

const hasRecentFolders = computed(() => recentFolders.value.length > 0)

function sendAnalyticsEvent(targetFolderUuid?: string) {
  // Use the passed folder UUID or fall back to currentFolder for modal saves
  const folderUuid = targetFolderUuid || currentFolder.value?.uuid
  const userFolderRole = folderUuid ? libraryStore.getUserRoleForFolder(folderUuid) : null
  switch (props.contentType) {
    case CONTENT_TYPES.GOAL:
    case CONTENT_TYPES.SUPER_PAGE:
      analytics.sendEvent(EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.GOAL, {
        cu: props.contentUuid,
        ct: props.contentTypeLabel,
        user_folder_role: userFolderRole,
      })
      break
    case CONTENT_TYPES.STRATEGY:
      analytics.sendEvent(EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.STRATEGY, {
        cu: props.contentUuid,
        ct: props.contentTypeLabel,
        user_folder_role: userFolderRole,
      })
      break
    case CONTENT_TYPES.PRESENTLEVEL:
    case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
      analytics.sendEvent(EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.PRESENT_LEVELS, {
        plw_uuid: props.contentUuid,
        plw_new_student: false,
        user_folder_role: userFolderRole
      })
      break
    case CONTENT_TYPES.RESOURCE:
      analytics.sendEvent(EVENT_NAMES.RESOURCES.SAVED, {
        cu: props.contentUuid,
        ct: props.contentTypeLabel,
        student_uuid: folderUuid,
        user_folder_role: userFolderRole
      })
      break
  }
}

const saveToFirstFolder = async () => {
  if (hasRecentFolders.value) {
    isSaving.value = true
    try {
      const firstFolder = recentFolders.value[0]
      const folder: { uuid: string, name: string } = {
        uuid: firstFolder.uuid,
        name: firstFolder.name
      }

      await libraryStore.saveToLibrary({
        contentType: props.contentType,
        contentUuid: props.contentUuid,
        collectionUuid: firstFolder.uuid,
        contributor: userId.value,
        folder: folder
      })

      try {
        sendAnalyticsEvent(firstFolder.uuid)
      } catch (ae) {
        console.warn('Analytics event failed to send', ae)
      }
      emit('save', folder)
    } catch (error) {
      emit('saveerror', error)
      console.error('Error saving to folder:', error)
    } finally {
      isSaving.value = false
    }
  }
}

const openSaveToFolderModal = () => {
  showSaveToFolderModal.value = true
}

const buttonClasses = computed(() => {
  const classes = ['bg-green-300', 'text-white', 'hover:bg-green-400', 'focus-visible:ring-green-400', 'focus:outline-none', 'focus-visible:ring-2', 'focus-visible:ring-offset-2']

  switch (props.size) {
    case 'xs':
      classes.push('text-text-sm', isSaving.value ? 'px-8' : 'px-2', 'py-1', 'rounded-sm')
      break;
    default:
      classes.push('text-text-md', isSaving.value ? 'px-8' : 'px-4', 'py-2', 'rounded-md')
  }

  return classes.join(' ')
})

const iconClasses = computed(() => {
  let iconClasses = ['material-icons-round']
  switch (props.size) {
    case 'xs':
      iconClasses.push('text-lg')
      break;
    default:
      break;
  }
  return iconClasses.join(' ')
})
</script>

<template>
  <div>
    <DropdownMenu>
      <DropdownMenuTrigger :class="buttonClasses" :aria-label="`Save to Library`">
        <div class="flex items-center gap-1">
          <slot v-if="!isSaving" name="icon">
            <span class="material-icons-round" :class="iconClasses">save</span>
          </slot>
          <span v-if="isSaving" class="inline-flex items-center">
            <ProgressSpinner class="mr-2 !h-5 !w-5 stroke-white" strokeWidth="4" /><span>Saving...</span>
          </span>
          <slot v-if="!isSaving" name="text">
            <span>Save to Library</span>
          </slot>
          <span v-if="!isSaving" class="material-icons-round" :class="iconClasses">arrow_drop_down</span>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent class="w-56" align="start">
        <DropdownMenuItem v-if="hasRecentFolders" @click="saveToFirstFolder">
          <span>Save to {{ recentFolders[0]?.name }}</span>
        </DropdownMenuItem>
        <DropdownMenuItem @click="openSaveToFolderModal">
          <span>Save to {{ hasRecentFolders ? 'another' : 'a' }} folder</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>

  <SaveToFolderModal
    v-if="showSaveToFolderModal"
    :content-type="props.contentType"
    :content-type-label="props.contentTypeLabel"
    :content-uuid="props.contentUuid"
    @update:visible="(val) => { showSaveToFolderModal = val }"
    @save="(folder) => emit('save', folder)"
  />
</template>